import { useState } from 'react';
import { CustomModal } from '@/components/CustomModal/CustomModal';
import { ResponsiveTable } from '@/components/Table/ResponsiveTable/ResponsiveTable';
import { useAppConstantsContext } from '@/context/AppConstants';
import { GoldenTag } from '@/reports/shared/DataCard/DataCardTag/DataCardTag';
import { EmptyVehicleSectionDisclaimer } from '@/reports/shared/ReportSection/EmptyVehicleSectionDisclaimer/EmptyVehicleSectionDisclaimer';
import { ReportSection } from '@/reports/shared/ReportSection/ReportSection';
import {
  AutomobileTitleChecks,
  StatesContact,
  StateContact,
} from '@/reports/types/vehicles';
import { handleClickStateContact } from '@/reports/vehicle/VehicleReport';
import { getHowItWorksContent } from '@/reports/vehicle/utils';
import { Text, Stack, Icons, PureTextButton } from '@/theme/index';
import { formatDateFromReport } from '@/utils/formatDateFromReport/formatDateFromReport';
import { pluralizeString } from '@/utils/pluralizeString';
import { useScreenSize } from '@/utils/useScreenSize';
import { AllTitleBrands } from './AllTitleBrand/AllTitleBrands';
import {
  PossibleBrandItem,
  titleBrandByCode,
  allPossibleBrands,
} from './AllTitleBrand/allPossibleBrands';
import { StyledDataCard } from '../VehicleTitleRecords/components';

export interface VehicleTitleBrandsProps {
  /** A list of all title brands data */
  vehicleTitleBrandsList: AutomobileTitleChecks;
  statesContact: StatesContact;
}

/**
 * This component is responsible for providing users with the ability to see all the vehicle title brands.
 * Receive a list of vehicle title brands.
 */
export const VehicleTitleBrands = ({
  vehicleTitleBrandsList,
  statesContact,
}: VehicleTitleBrandsProps) => {
  const {
    data: { brandName },
  } = useAppConstantsContext();
  const [openBrandModal, setOpenBrandModal] = useState(false);
  const [openStateModal, setOpenStateModal] = useState(false);
  const [possibleBrandSelected, setPossibleBrandSelected] = useState<
    PossibleBrandItem | undefined
  >(undefined);
  const [stateSelected, setStateSelected] = useState<StateContact | undefined>(
    undefined
  );
  const { isMobile } = useScreenSize();
  const allBrandsFlagged = allPossibleBrands.map((brand) => {
    const found = vehicleTitleBrandsList.some((chk) => {
      return chk.code === brand.code;
    });

    return { ...brand, found };
  });

  const header = ['Brand', 'Date Applied', 'Reported By', 'Type'].map(
    (text, index) => (
      <Text key={index} variant={isMobile ? 'body2' : 'h5'} sx={{ p: 1, m: 0 }}>
        {text}
      </Text>
    )
  );

  const modalStateTitle = stateSelected && (
    <Text variant="h3">{`How to Request a State Vehicle Record: ${stateSelected.name}`}</Text>
  );

  const modalStateDescription = stateSelected && (
    <div
      dangerouslySetInnerHTML={{ __html: stateSelected?.instructions || '' }}
    />
  );

  const alertItem = (code: string) => {
    const option = titleBrandByCode(code);

    return (
      <Stack direction="row" alignItems="center">
        <Icons.Warning color="error" fontSize="small" sx={{ mr: 1 }} />
        <PureTextButton
          variant="text"
          color="error"
          onClick={() => handleClickPossibleBrand(option)}
        >
          {option?.name || ''}
        </PureTextButton>
      </Stack>
    );
  };

  const categoryMap: Record<string, string> = {
    S: 'State',
    F: 'Federal',
  };

  const modifiedTitleBrandsList = vehicleTitleBrandsList.map((title) => {
    const categoryCode = title?.reporting_entity?.category_code || '';

    return [
      <Text variant="body2" sx={{ p: 1 }}>
        {alertItem(title.code || '')}
      </Text>,
      <Text variant="body2" sx={{ p: 1 }}>
        {formatDateFromReport(title.date?.full) || ''}
      </Text>,
      <PureTextButton
        variant="text"
        color="success"
        sx={{
          p: 1,
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
        onClick={() => {
          handleClickStateContact(
            title.reporting_entity?.id || '',
            statesContact,
            setStateSelected,
            handleCloseStateModal
          );
        }}
      >
        {title.reporting_entity?.name || ''}
      </PureTextButton>,
      <Text variant="body2" sx={{ p: 1 }}>
        {categoryMap[categoryCode] || categoryCode}
      </Text>,
    ];
  });

  function handleClickPossibleBrand(option: PossibleBrandItem | undefined) {
    if (option) {
      setPossibleBrandSelected(option);
    }

    handleCloseBrandModal();
  }

  function handleCloseBrandModal() {
    setOpenBrandModal(!openBrandModal);
  }

  function handleCloseStateModal() {
    setOpenStateModal(!openStateModal);
  }

  function HowItWorksCTASection() {
    const howItWorksContent = getHowItWorksContent('Title Brands');

    return (
      <>
        <AllTitleBrands
          allBrandsFlagged={allBrandsFlagged}
          handleClickPossibleBrand={handleClickPossibleBrand}
        />
        {howItWorksContent}
        <CustomModal
          open={openBrandModal}
          customTitle={modalBrandTitle()}
          description={
            possibleBrandSelected && possibleBrandSelected.description
          }
          returnButtonText="Return to vehicle report"
          handleClose={handleCloseBrandModal}
        />
      </>
    );
  }

  const subsectionTitle =
    vehicleTitleBrandsList.length > 0
      ? `${vehicleTitleBrandsList.length} Reported Title ${pluralizeString(
          'brand',
          vehicleTitleBrandsList.length
        )}`
      : 'No title brands were found!';

  const noContentDescription = `${brandName} is updated regularly as new information is reported.`;
  const modalBrandTitle = () => {
    return (
      possibleBrandSelected && (
        <Text variant="h3">{possibleBrandSelected.name}</Text>
      )
    );
  };

  return (
    <ReportSection
      title="Title Brands"
      showTopBar
      hasContent={vehicleTitleBrandsList.length > 0}
      sectionId="vehicle-title-brands"
      howItWorks={HowItWorksCTASection()}
      customEmptyText={<EmptyVehicleSectionDisclaimer title="title brands" />}
    >
      <StyledDataCard
        tag={
          <GoldenTag>
            <Text fontWeight="bold">Premium</Text>
          </GoldenTag>
        }
      >
        <Text variant="h2">{subsectionTitle}</Text>
        {vehicleTitleBrandsList.length > 0 ? (
          <>
            <Text
              sx={{
                color: (theme) => theme.palette.text.secondary,
                fontSize: '0.75rem',
                fontWeight: 'bold',
                mb: { sm: 2 },
              }}
            >
              Source: NMVTIS
            </Text>
            <ResponsiveTable
              tableData={modifiedTitleBrandsList}
              headers={header}
              sectionId="vehicle-title-brands"
            />
          </>
        ) : (
          <Text variant="body2" sx={{ p: 0, m: 0 }}>
            {noContentDescription}
          </Text>
        )}
      </StyledDataCard>
      <AllTitleBrands
        allBrandsFlagged={allBrandsFlagged}
        handleClickPossibleBrand={handleClickPossibleBrand}
      />
      <CustomModal
        open={openBrandModal}
        customTitle={modalBrandTitle()}
        description={possibleBrandSelected && possibleBrandSelected.description}
        returnButtonText="Return to vehicle report"
        handleClose={handleCloseBrandModal}
      />
      <CustomModal
        open={openStateModal}
        customTitle={modalStateTitle}
        description={modalStateDescription}
        returnButtonText="Return to vehicle report"
        handleClose={handleCloseStateModal}
      />
    </ReportSection>
  );
};
